import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  ReceiptAttachmentModel,
  ReceiptAttachmentResponse,
} from 'src/models/receiptAttachment.models'
import { ReceiptRecoveryCase } from 'src/models/transaction.models'
import { receiptAgentQueries } from 'src/queries/organizations/receiptAgent/receiptAgent.queries'

import { mergeReceiptRecoveryAttachments } from './receiptRecoveryPreview'

interface UseReceiptRecoveryAttachmentsParams {
  enabled: boolean
  organizationId?: string
  receiptRecoveryCase?: ReceiptRecoveryCase
  receiptAttachments: readonly ReceiptAttachmentModel[]
}

const isReceiptAttachmentResponse = (
  value: ReceiptAttachmentResponse | null | undefined,
): value is ReceiptAttachmentResponse =>
  value !== null &&
  value !== undefined &&
  typeof value.id === 'string' &&
  typeof value.urls === 'object' &&
  value.urls !== null

export const useReceiptRecoveryAttachments = ({
  enabled,
  organizationId,
  receiptRecoveryCase,
  receiptAttachments,
}: UseReceiptRecoveryAttachmentsParams) => {
  const shouldLoadDetachedCandidate =
    enabled &&
    organizationId !== undefined &&
    receiptRecoveryCase?.id !== undefined &&
    receiptRecoveryCase.receiptAttachmentId !== undefined &&
    !receiptAttachments.some(
      ({ id }) => id === receiptRecoveryCase.receiptAttachmentId,
    )
  const { data: receiptRecoveryCaseDetail } = useQuery({
    ...receiptAgentQueries.case(organizationId, receiptRecoveryCase?.id),
    enabled: shouldLoadDetachedCandidate,
  })
  const detachedReceiptRecoveryAttachment = useMemo(() => {
    const selectedReceipt = receiptRecoveryCaseDetail?.selectedReceipt

    if (
      !isReceiptAttachmentResponse(selectedReceipt) ||
      selectedReceipt.id !== receiptRecoveryCase?.receiptAttachmentId
    ) {
      return undefined
    }

    return new ReceiptAttachmentModel(selectedReceipt)
  }, [
    receiptRecoveryCase?.receiptAttachmentId,
    receiptRecoveryCaseDetail?.selectedReceipt,
  ])
  const receiptRecoveryAttachments = useMemo(
    () =>
      mergeReceiptRecoveryAttachments(
        receiptAttachments,
        detachedReceiptRecoveryAttachment,
      ),
    [receiptAttachments, detachedReceiptRecoveryAttachment],
  )

  return {
    detachedReceiptRecoveryAttachment,
    receiptRecoveryAttachments,
  }
}
